import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'

import type { SoundId } from './audio/sounds'
import { store } from '../stores/store'
import {
    getActiveMusicId,
    pauseMusicWithOverlay,
    playMusic,
    playSound,
    resumeMusicFromOverlay,
    stopMusic,
} from '../utils/soundPlayer'
import { useRunState } from './provider/useRunState'

const FOURTH_LEVEL_RATE = 1.1

type LevelMusicTarget = {
    soundId: SoundId
    rate: number
}

function levelMusicForRegion(region: 1 | 2 | 3 | 4): LevelMusicTarget {
    switch (region) {
        case 1:
            return { soundId: 'musicLevel1', rate: 1 }
        case 2:
            return { soundId: 'musicLevel2', rate: 1 }
        case 3:
            return { soundId: 'musicLevel3', rate: 1 }
        case 4:
            return { soundId: 'musicLevel3', rate: FOURTH_LEVEL_RATE }
    }
}

/**
 * Drives in-run music from the run phase + track region:
 * - region 1/2/3 → musicLevel1/2/3, region 4 → musicLevel3 pitched +10%
 * - tunnel transitions → musicTunnelTheme
 * - finish / level complete → musicVictoryTheme
 * - game over → musicGameOverTheme
 * - pause overlay (musicPause) while paused via the pause menu, resuming the level track on continue
 * - power-up tutorial freeze (also phase === 'paused') keeps current level / star music
 * - stops when leaving the run. Transitions are always faded by the sound player.
 */
export const LevelMusicBridge = observer(function LevelMusicBridge() {
    const runState = useRunState()

    const isRun = store.appFlowPhase === 'run'
    const phase = runState.phase
    const { activeRegion, activeTransition } = runState.trackRegionState
    const finishReached = runState.snapshot.finishLineReached
    const isPowerUpTutorialFreeze = store.powerUpTutorial.isOverlayVisible

    const previousPhaseRef = useRef(phase)
    const previousIsRunRef = useRef(isRun)
    const winSequenceActiveRef = useRef(false)

    useEffect(() => {
        const wasRun = previousIsRunRef.current
        previousIsRunRef.current = isRun

        if (!isRun) {
            if (wasRun) {
                stopMusic()
            }
            winSequenceActiveRef.current = false
            previousPhaseRef.current = phase
            return
        }

        const previousPhase = previousPhaseRef.current
        previousPhaseRef.current = phase

        const inWinSequence =
            phase === 'levelComplete' || phase === 'endCard' || finishReached

        if (inWinSequence) {
            playMusic('musicVictoryTheme')
            if (!winSequenceActiveRef.current) {
                setTimeout(() => {
                    playSound('characterFinishLine')
                }, 750)
                setTimeout(() => {
                    playSound('tradingCardReveal')
                }, 4000)
            }
            winSequenceActiveRef.current = true
            return
        }

        winSequenceActiveRef.current = false

        if (phase === 'gameOver') {
            playMusic('musicGameOverTheme')
            return
        }

        if (phase === 'paused') {
            // Tutorial overlays freeze the run without switching to pause music.
            if (isPowerUpTutorialFreeze) {
                return
            }
            pauseMusicWithOverlay('musicPause')
            return
        }

        if (phase !== 'running') {
            return
        }

        if (activeTransition) {
            playMusic('musicTunnelTheme')
            return
        }

        if (getActiveMusicId() === 'musicStarCollect') {
            return
        }

        // Only resume when leaving a real pause (musicPause). Tutorial freezes
        // never switch to pause music, so keep whatever track is already playing.
        if (wasRun && previousPhase === 'paused') {
            if (getActiveMusicId() === 'musicPause') {
                resumeMusicFromOverlay()
            }
            return
        }

        const target = levelMusicForRegion(activeRegion)
        playMusic(target.soundId, { rate: target.rate })
    }, [isRun, phase, activeRegion, activeTransition, finishReached, isPowerUpTutorialFreeze])

    return null
})
